import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PortalNav } from "@/components/PortalNav";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Columns, GripVertical, Pencil, X } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["Lead", "Qualified", "Proposal", "Won", "Lost"];
const stageColor = (s) =>
  s === "Won" ? "border-t-[#10B981]" : s === "Lost" ? "border-t-[#EF4444]" : "border-t-[#D4AF37]";

const OPP_TO_COL = { "Investigation/Data Gathering": "Qualified", "Proposal/Negotiation": "Proposal", "Won": "Won", "Lost": "Lost", "Abandoned": "Lost" };
const COL_TO_OPP = { "Lead": "Investigation/Data Gathering", "Qualified": "Investigation/Data Gathering", "Proposal": "Proposal/Negotiation", "Won": "Won", "Lost": "Lost" };
const ORDER_TO_COL = { "Draft": "Proposal", "Sent": "Proposal", "Accepted": "Won", "Ordered": "Won" };
const COL_TO_ORDER = { "Proposal": "Sent", "Won": "Accepted" };
const TYPE_BADGE = { deal: "bg-zinc-100 text-zinc-500", opp: "bg-[#D4AF37]/15 text-[#B8860B]", order: "bg-[#0F172A]/10 text-[#0F172A]" };

export default function CrmPipeline() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [opps, setOpps] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overStage, setOverStage] = useState(null);
  const [editDeal, setEditDeal] = useState(null);
  const [floating, setFloating] = useState(null);
  const [draggingKey, setDraggingKey] = useState(null);
  const boardRef = useRef(null);
  const dragInfo = useRef(null);

  const load = async () => {
    try {
      const [dRes, xRes] = await Promise.all([api.get("/crm/deals"), api.get("/crm/board-extra")]);
      setDeals(dRes.data.deals || []);
      setOpps(xRes.data.opportunities || []);
      setOrders(xRes.data.orders || []);
    } catch (e) { /* board load failed; UI shows empty state */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const moveDeal = async (id, stage) => {
    const prev = deals;
    setDeals((ds) => ds.map((d) => (d.id === id ? { ...d, stage } : d)));
    try { await api.patch(`/crm/deals/${id}`, { stage }); toast.success(`Moved to ${stage}`); }
    catch (err) { setDeals(prev); toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not move deal"); }
  };
  const moveOpp = async (id, column) => {
    const stage = COL_TO_OPP[column];
    if (!stage) return;
    const prev = opps;
    setOpps((xs) => xs.map((o) => (o.id === id ? { ...o, stage } : o)));
    try { await api.patch(`/crm/opportunities/${id}/stage`, { stage }); toast.success(`Opportunity → ${stage}`); }
    catch (err) { setOpps(prev); toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not move opportunity"); }
  };
  const moveOrder = async (id, column) => {
    const status = COL_TO_ORDER[column];
    if (!status) { toast.error("Sales orders can only move to Proposal or Won"); return; }
    const prev = orders;
    setOrders((xs) => xs.map((o) => (o.id === id ? { ...o, status } : o)));
    try { await api.patch(`/crm/orders/${id}/status`, { status }); toast.success(`Order → ${status}`); }
    catch (err) { setOrders(prev); toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not move order"); }
  };
  const stageAtPoint = (x, y) => {
    const cols = boardRef.current ? boardRef.current.querySelectorAll("[data-stage]") : [];
    for (const c of cols) {
      const r = c.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return c.getAttribute("data-stage");
    }
    return null;
  };

  const onCardPointerDown = (e, card) => {
    if (e.button != null && e.button !== 0) return;
    const startX = e.clientX, startY = e.clientY;
    const width = e.currentTarget.getBoundingClientRect().width;
    let started = false;
    const move = (ev) => {
      const cx = ev.clientX, cy = ev.clientY;
      if (!started) {
        if (Math.abs(cx - startX) + Math.abs(cy - startY) < 6) return;
        started = true;
        dragInfo.current = { type: card.type, id: card.id };
        setDraggingKey(`${card.type}:${card.id}`);
        setFloating({ ...card, x: cx, y: cy, w: width });
        document.body.style.userSelect = "none";
      }
      setFloating((f) => (f ? { ...f, x: cx, y: cy } : f));
      setOverStage(stageAtPoint(cx, cy));
    };
    const up = (ev) => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      document.body.style.userSelect = "";
      if (started) {
        const col = stageAtPoint(ev.clientX, ev.clientY);
        const info = dragInfo.current;
        if (col && info) {
          if (info.type === "deal") moveDeal(info.id, col);
          else if (info.type === "opp") moveOpp(info.id, col);
          else if (info.type === "order") moveOrder(info.id, col);
        }
      }
      dragInfo.current = null;
      setFloating(null);
      setDraggingKey(null);
      setOverStage(null);
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
  };

  const dealsIn = (s) => deals.filter((d) => d.stage === s);
  const oppsIn = (s) => opps.filter((o) => (OPP_TO_COL[o.stage] || "Lead") === s);
  const ordersIn = (s) => orders.filter((o) => (ORDER_TO_COL[o.status] || "Proposal") === s);
  const colCount = (s) => dealsIn(s).length + oppsIn(s).length + ordersIn(s).length;
  const colTotal = (s) => dealsIn(s).reduce((a, d) => a + (d.amount || 0), 0) + oppsIn(s).reduce((a, o) => a + (o.amount || 0), 0) + ordersIn(s).reduce((a, o) => a + (o.total || 0), 0);

  const Card = ({ type, id, title, company, amount, testid, onEdit, onOpen }) => (
    <div onPointerDown={(e) => onCardPointerDown(e, { type, id, title, company, amount })}
      data-testid={testid} style={{ touchAction: "none" }}
      className={`border border-zinc-200 bg-white p-2.5 cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow ${draggingKey === `${type}:${id}` ? "opacity-40" : ""}`}>
      <div className="flex items-start gap-2">
        <GripVertical className="h-4 w-4 text-zinc-300 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1">
            <div className="font-medium text-sm text-[#0F172A] truncate">{title}</div>
            {onEdit && <button onClick={(e) => { e.stopPropagation(); onEdit(); }} onPointerDown={(e) => e.stopPropagation()} draggable={false} data-testid={`${testid}-edit`} className="text-zinc-300 hover:text-[#0F172A] transition-colors shrink-0"><Pencil className="h-3.5 w-3.5" /></button>}
          </div>
          {company && <button onClick={() => onOpen && onOpen()} className="text-xs text-zinc-500 hover:text-[#0F172A] truncate block">{company}</button>}
          <div className="flex items-center justify-between mt-1">
            {amount != null ? <span className="text-xs font-mono text-[#D4AF37]">${Number(amount).toLocaleString()}</span> : <span />}
            <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full ${TYPE_BADGE[type]}`}>{type === "opp" ? "Opportunity" : type === "order" ? "Order" : "Deal"}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F4F5] md:pl-[var(--sb-w,16rem)]">
      <PortalNav />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-2">Sales Pipeline</p>
          <h1 className="font-display font-extrabold text-4xl tracking-tight text-[#0F172A] flex items-center gap-3">
            <Columns className="h-8 w-8 text-[#D4AF37]" /> Deal Board
          </h1>
          <p className="mt-2 text-sm text-zinc-500">Deals, opportunities and sales orders — drag any card to a new stage.</p>

          {loading ? (
            <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0F172A]" /></div>
          ) : (
            <div ref={boardRef} className="mt-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="pipeline-board">
              {STAGES.map((s) => (
                <div key={s} data-testid={`stage-col-${s}`} data-stage={s}
                  className={`bg-white border border-zinc-200 border-t-2 ${stageColor(s)} min-h-[200px] transition-shadow ${overStage === s ? "ring-2 ring-[#D4AF37]" : ""}`}>
                  <div className="p-3 border-b border-zinc-100 flex items-center justify-between">
                    <span className="font-display font-bold text-sm text-[#0F172A]">{s}</span>
                    <span className="text-[11px] font-mono text-zinc-400">{colCount(s)} · ${colTotal(s).toLocaleString()}</span>
                  </div>
                  <div className="p-3 space-y-4">
                    {colCount(s) === 0 && <p className="text-xs text-zinc-300 text-center py-6">Drop here</p>}
                    {dealsIn(s).length > 0 && (
                      <div className="space-y-2" data-testid={`deals-section-${s}`}>
                        <p className="text-[10px] uppercase tracking-wide text-zinc-400">Deals</p>
                        {dealsIn(s).map((d) => (
                          <Card key={d.id} type="deal" id={d.id} title={d.title} company={d.company_name} amount={d.amount} testid={`deal-card-${d.id}`} onEdit={() => setEditDeal(d)} onOpen={() => navigate(`/crm/customers/${d.customer_id}`)} />
                        ))}
                      </div>
                    )}
                    {oppsIn(s).length > 0 && (
                      <div className="space-y-2" data-testid={`opps-section-${s}`}>
                        <p className="text-[10px] uppercase tracking-wide text-zinc-400">Opportunities</p>
                        {oppsIn(s).map((o) => (
                          <Card key={o.id} type="opp" id={o.id} title={o.name} company={o.customer_name} amount={o.amount} testid={`board-opp-${o.id}`} onOpen={() => navigate(o.customer_id ? `/crm/customers/${o.customer_id}` : "/crm/pipeline")} />
                        ))}
                      </div>
                    )}
                    {ordersIn(s).length > 0 && (
                      <div className="space-y-2" data-testid={`orders-section-${s}`}>
                        <p className="text-[10px] uppercase tracking-wide text-zinc-400">Sales Orders</p>
                        {ordersIn(s).map((o) => (
                          <Card key={o.id} type="order" id={o.id} title={o.number} company={o.customer_name} amount={o.total} testid={`board-order-${o.id}`} onOpen={() => navigate(o.customer_id ? `/crm/customers/${o.customer_id}` : "/crm/pipeline")} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </main>
      {floating && (
        <div className="fixed z-[80] pointer-events-none" style={{ left: floating.x + 10, top: floating.y + 10, width: floating.w }} data-testid="pipeline-drag-ghost">
          <div className="border border-[#D4AF37] bg-white p-2.5 shadow-xl rotate-2 opacity-95">
            <div className="font-medium text-sm text-[#0F172A] truncate">{floating.title}</div>
            {floating.company && <div className="text-xs text-zinc-500 truncate">{floating.company}</div>}
            {floating.amount != null && <div className="text-xs font-mono text-[#D4AF37] mt-1">${Number(floating.amount).toLocaleString()}</div>}
          </div>
        </div>
      )}
      {editDeal && (
        <EditDealModal
          deal={editDeal}
          onClose={() => setEditDeal(null)}
          onSaved={(patch) => {
            setDeals((ds) => ds.map((d) => (d.id === editDeal.id ? { ...d, ...patch } : d)));
            setEditDeal(null);
          }}
        />
      )}
    </div>
  );
}

function EditDealModal({ deal, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: deal.title || "",
    amount: deal.amount == null ? "" : String(deal.amount),
    notes: deal.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const patch = {
      title: form.title,
      amount: form.amount === "" ? null : parseFloat(form.amount),
      notes: form.notes,
    };
    try {
      await api.patch(`/crm/deals/${deal.id}`, patch);
      toast.success("Deal updated");
      onSaved(patch);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="deal-edit-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-white border border-zinc-200 w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200 bg-[#0F172A] text-white">
          <div className="flex items-center gap-2"><Pencil className="h-4 w-4 text-[#D4AF37]" /><h3 className="font-display font-bold">Edit Deal</h3></div>
          <button onClick={onClose} data-testid="deal-edit-close"><X className="h-5 w-5 text-zinc-300 hover:text-white" /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4" data-testid="deal-edit-form">
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="deal-edit-title" className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Amount ($)</label>
            <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="deal-edit-amount" className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="deal-edit-notes" rows={3} className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent" />
          </div>
          <button type="submit" disabled={saving} data-testid="deal-edit-save" className="w-full flex items-center justify-center gap-2 bg-[#0F172A] text-white py-3 font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
